import { FC, useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";

import { tabs } from "../lib/FormValidation";
import { ArchivePanelLeft } from "@/features/training/pages/Archive/panel-left/ArchivePanelLeft";
import { ArchivePanelRight } from "@/features/training/pages/Archive/panel-right/ArchivePanelRight";
import TrainingDashboard from "@/features/training/pages/Dashboard/TrainingDashboard";
import { PlannerCalendar } from "@/features/training/pages/Planner/PlannerCalendar";
import { DrillPanelLeft } from "@/features/training/pages/drill-tab/panel-left/DrillPanelLeft";
import { DrillPanelRight } from "@/features/training/pages/drill-tab/panel-right/DrillPanelRight";
import { CoursePanelLeft } from "@/features/training/pages/training-tab/panel-left/CoursePanelLeft";
import { CoursePanelRight } from "@/features/training/pages/training-tab/panel-right/CoursePanelRight";
import { PanelsGrid } from "@/features/training/ui";
import { NavBar } from "@/shared/ui/NavBar";
import { TabPanel } from "@/shared/ui/TabPanel";
import { Box } from "@mui/material";

/** Shared horizontal padding for all training tab content (matches dashboard) */
const TAB_CONTENT_PX = { xs: 1.5, sm: 2, lg: 2.5 };
const TAB_CONTENT_PY = { xs: 1.25, sm: 1.5, lg: 2 };
const PANEL_COLUMN_SX = {
	height: "100%",
	minHeight: 0,
	overflow: "hidden",
	display: "flex",
	flexDirection: "column",
};
const LEFT_WRAPPER_SX = {
	"& .wrapper-left": {
		flex: 1,
		minHeight: 0,
		overflow: "hidden",
		display: "flex",
		flexDirection: "column",
		p: 0,
	},
};
const RIGHT_WRAPPER_SX = {
	"& .wrapper-right": {
		flex: 1,
		minHeight: 0,
		overflow: "hidden",
		display: "flex",
		flexDirection: "column",
		marginTop: 0,
		p: 0,
	},
};

interface TrainingPageProps {
	tabIndex?: number;
}

export const TrainingPage: FC<TrainingPageProps> = ({ tabIndex }) => {
	const navigate = useNavigate();
	const { pathname } = useLocation();
	const [currentTab, setCurrentTab] = useState<number>(tabIndex ?? 0);
	const tab = useRef(currentTab);

	useEffect(() => {
		if (pathname.includes("/planner")) {
			setCurrentTab(4);
		} else if (pathname.includes("/archive")) {
			setCurrentTab(3);
		} else if (pathname.includes("/drills")) {
			setCurrentTab(2);
		} else if (pathname.includes("/courses")) {
			setCurrentTab(1);
		} else {
			setCurrentTab(0); // Dashboard
		}
	}, [pathname]);

	useEffect(() => {
		if (tab.current === currentTab) return;

		tab.current = currentTab;

		// Path prefixes for each tab - only navigate if we're not already under this tab's path
		// (avoids overwriting e.g. /training/drills/104/sessions/1278 with /training/drills)
		const pathPrefixes: Record<number, string> = {
			0: "/training/dashboard",
			1: "/training/courses",
			2: "/training/drills",
			3: "/training/archive",
			4: "/training/planner",
		};
		const targetPrefix = pathPrefixes[currentTab];
		if (targetPrefix && pathname.startsWith(targetPrefix)) {
			return; // Already on a path for this tab, preserve the URL
		}

		switch (currentTab) {
			case 0:
				navigate("/training/dashboard");
				break;
			case 1:
				navigate("/training/courses");
				break;
			case 2:
				navigate("/training/drills");
				break;
			case 3:
				navigate("/training/archive");
				break;
			case 4:
				navigate("/training/planner");
				break;
			case 5:
				window.location.href = `${window.location.origin}/settings`;
				break;
		}
	}, [currentTab, navigate, pathname]);

	return (
		<Box
			data-tour="training-page"
			sx={{
				width: "100%",
				flex: 1,
				minHeight: 0,
				display: "flex",
				flexDirection: "column",
				bgcolor: "background.default",
				gap: 0,
			}}
		>
			<Box component="header" data-tour="training-tabs" sx={{ flexShrink: 0 }}>
				<NavBar
					links={[]}
					tabs={tabs}
					currentTab={currentTab}
					setCurrentTab={setCurrentTab}
				/>
			</Box>
			<TabPanel currentTab={currentTab} index={0} fill>
				<TrainingDashboard />
			</TabPanel>
			<TabPanel currentTab={currentTab} index={1} fill>
				<PanelsGrid
					leftPanel={<CoursePanelLeft />}
					rightPanel={<CoursePanelRight />}
					leftSize={{ xs: 12, sm: 5 }}
					rightSize={{ xs: 12, sm: 7 }}
					resizableDesktop
					resizeStorageKey="training-browser-left-panel-width"
					defaultLeftWidth={430}
					minLeftWidth={320}
					minRightWidth={420}
					containerSx={{
						flex: 1,
						minHeight: 0,
						overflow: "hidden",
						px: TAB_CONTENT_PX,
						py: TAB_CONTENT_PY,
					}}
					leftPanelSx={{ ...PANEL_COLUMN_SX, ...LEFT_WRAPPER_SX }}
					rightPanelSx={{ ...PANEL_COLUMN_SX, ...RIGHT_WRAPPER_SX }}
				/>
			</TabPanel>
			<TabPanel currentTab={currentTab} index={2} fill>
				<PanelsGrid
					leftPanel={<DrillPanelLeft />}
					rightPanel={<DrillPanelRight />}
					leftSize={{ xs: 12, sm: 5 }}
					rightSize={{ xs: 12, sm: 7 }}
					resizableDesktop
					resizeStorageKey="training-browser-left-panel-width"
					defaultLeftWidth={430}
					minLeftWidth={320}
					minRightWidth={420}
					containerSx={{
						flex: 1,
						minHeight: 0,
						overflow: "hidden",
						px: TAB_CONTENT_PX,
						py: TAB_CONTENT_PY,
					}}
					leftPanelSx={{ ...PANEL_COLUMN_SX, ...LEFT_WRAPPER_SX }}
					rightPanelSx={{ ...PANEL_COLUMN_SX, ...RIGHT_WRAPPER_SX }}
				/>
			</TabPanel>
			<TabPanel currentTab={currentTab} index={3} fill>
				<PanelsGrid
					leftPanel={<ArchivePanelLeft />}
					rightPanel={<ArchivePanelRight />}
					leftSize={{ xs: 12, sm: 5 }}
					rightSize={{ xs: 12, sm: 7 }}
					resizableDesktop
					resizeStorageKey="training-browser-left-panel-width"
					defaultLeftWidth={430}
					minLeftWidth={320}
					minRightWidth={420}
					containerSx={{
						flex: 1,
						minHeight: 0,
						overflow: "hidden",
						px: TAB_CONTENT_PX,
						py: TAB_CONTENT_PY,
					}}
					leftPanelSx={{ ...PANEL_COLUMN_SX, ...LEFT_WRAPPER_SX }}
					rightPanelSx={{ ...PANEL_COLUMN_SX, ...RIGHT_WRAPPER_SX }}
				/>
			</TabPanel>
			<TabPanel currentTab={currentTab} index={4} fill>
				<Box
					sx={{
						flex: 1,
						minHeight: 0,
						height: "100%",
						display: "flex",
						flexDirection: "column",
						overflow: "hidden",
						px: TAB_CONTENT_PX,
						py: TAB_CONTENT_PY,
					}}
				>
					<PlannerCalendar />
				</Box>
			</TabPanel>
		</Box>
	);
};
